import { useMemo } from 'react';
import { useGqlQuery } from '@API/useAvQuery';
import { QueryKey } from '@fabric-ui/utils';
import { AuthenticationSourceTypeDetails } from '@views/Authentications/types';
import { AgingConfig, SuppressionFilter } from '@views/Sources/types';
import { gql } from 'graphql-request';

interface ScheduleFrequency {
  incrementalCron: string;
  fullCron: string;
  supportedSchedules: string[];
}

interface DataSourceType {
  type: string;
  name: string;
  category: string;
  classifications: string[];
  iconUrl: string;
  vendorName: string;
  suppressionFilter: SuppressionFilter;
  authenticationSourceType: AuthenticationSourceTypeDetails;
  agingConfigs: AgingConfig[];
  scheduleFrequency: ScheduleFrequency;
  tag: string;
  hasDefaultMapping: boolean;
  kbLink?: string;
  searchTerms?: string[];
}

interface DataSourceDetails {
  mappedSourceName: string;
  iconUrl: string;
  kbLink?: string;
}

interface IntegrationSupports {
  create: boolean;
  sync: boolean;
  webhook: boolean;
  schema: boolean;
  comments: boolean;
  attachments: boolean;
  manualAttach: boolean;
}

interface IntegrationType {
  type: string;
  name: string;
  authenticationSourceTypeName: string;
  category: string;
  supports: IntegrationSupports;
}

interface StaticListsData {
  getAllDataSourcesTypes: DataSourceType[];
  getDataSourceDetailsByAccountId: DataSourceDetails[];
  getAllIntegrationTypes: IntegrationType[];
  getAllAuthenticationSourcesTypes: AuthenticationSourceTypeDetails[];
}

type SourceOption = (DataSourceType | { type: string; name: string; iconUrl: string; kbLink?: string }) & { group?: string };

interface ProcessedData {
  sourcesObj: {
    sourcesMap: Record<string, SourceOption>;
    sourcesMapByName: Record<string, SourceOption>;
    sourceOptions: SourceOption[];
  };
  destinationsObj: {
    destinationsMap: Record<string, IntegrationType>;
    destinationsOptions: IntegrationType[];
  };
  authenticationSourcesObj: {
    authTypesMap: Record<string, AuthenticationSourceTypeDetails>;
    authTypesMapByName: Record<string, AuthenticationSourceTypeDetails>;
    authTypesOptions: AuthenticationSourceTypeDetails[];
  };
}

const GET_STATIC_LISTS = gql`
  query getAllDataSourcesTypes {
    getAllDataSourcesTypes {
      type
      name
      category
      classifications
      iconUrl
      vendorName
      systemManaged
      suppressionFilter: suppressionFilterV2 {
        filter
        include
      }
      authenticationSourceType {
        type
        name
        iconUrl
        authenticationType
        authenticationProviderType
        useAuthUrl
        authenticationEntityTypes
        useVerifySSL
      }
      agingConfigs {
        daysToClose
        isRelationBased
        agingRules {
          filter
          daysToClose
        }
      }
      scheduleFrequency {
        incrementalCron
        fullCron
        supportedSchedules
      }
      tag
      hasDefaultMapping
      searchTerms
    }
    getDataSourceDetailsByAccountId {
      mappedSourceName
      iconUrl
    }
    getAllIntegrationTypes {
      type
      name
      authenticationSourceTypeName
      hiddenFromCreate
      category
      supports {
        create
        sync
        webhook
        schema
        comments
        attachments
        manualAttach
      }
    }
    getAllAuthenticationSourcesTypes {
      type
      name
      iconUrl
      authenticationType
      authenticationProviderType
      useAuthUrl
      authenticationEntityTypes
      kbLink
      useVerifySSL
    }
  }
`;

const convertArrToObj = (obj, item) => ({ ...obj, [item.type]: item });
const convertArrToObjByName = (obj, item) => ({ ...obj, [item.name]: item });

const categorizeSourceOptions = (sourceOptions: SourceOption[], connected: DataSourceDetails[]): SourceOption[] => {
  const flatTypes = new Set(connected.map(({ mappedSourceName }) => mappedSourceName));
  return sourceOptions
    .map(opt => ({ ...opt, group: flatTypes.has(opt.name) ? 'Connected' : 'Not Connected' }))
    .toSorted((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
};

export const cachedStatic: ProcessedData = {
  sourcesObj: { sourcesMap: {}, sourcesMapByName: {}, sourceOptions: [] },
  destinationsObj: { destinationsMap: {}, destinationsOptions: [] },
  authenticationSourcesObj: { authTypesMap: {}, authTypesMapByName: {}, authTypesOptions: [] },
};

export const useTypeNameMap = options => {
  const queryResult = useGqlQuery<StaticListsData>({
    queryKey: [QueryKey.sourceTypeList],
    query: GET_STATIC_LISTS,
    select: (_, data) => data,
    retry: 10,
    retryDelay: 1000,
    ...options,
  });

  const data = useMemo((): ProcessedData => {
    if (!queryResult.data) {
      return {} as ProcessedData;
    }
    const sources: SourceOption[] = [
      ...queryResult.data.getAllDataSourcesTypes,
      ...queryResult.data.getDataSourceDetailsByAccountId
        .filter(({ mappedSourceName }) => !queryResult.data.getAllDataSourcesTypes.find(({ name }) => name === mappedSourceName))
        .map(({ mappedSourceName: name, iconUrl, kbLink }) => ({ type: name, name, iconUrl, kbLink })),
    ];
    return {
      ...cachedStatic,
      sourcesObj: {
        sourcesMap: sources.reduce(convertArrToObj, {}),
        sourcesMapByName: sources.reduce(convertArrToObjByName, {}),
        sourceOptions: categorizeSourceOptions(sources, queryResult.data.getDataSourceDetailsByAccountId),
      },
      destinationsObj: {
        destinationsMap: queryResult.data.getAllIntegrationTypes.reduce(convertArrToObj, {}),
        destinationsOptions: queryResult.data.getAllIntegrationTypes,
      },
      authenticationSourcesObj: {
        authTypesMap: queryResult.data.getAllAuthenticationSourcesTypes.reduce(convertArrToObj, {}),
        authTypesMapByName: queryResult.data.getAllAuthenticationSourcesTypes.reduce(convertArrToObjByName, {}),
        authTypesOptions: queryResult.data.getAllAuthenticationSourcesTypes,
      },
    };
  }, [queryResult.data]);

  return { ...queryResult, data };
};
